import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { login, reset, selectCurrentUser, selectAuthLoading, selectAuthError, selectAuthSuccess, selectAuthMessage } from '../features/auth/authSlice';
import { useToast } from '../contexts/ToastContext';
import OmTextField from '../components/ui/OmTextField';
import OmButton from '../components/ui/OmButton';
import OMLogo from '../assets/OM-logo.png';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const user = useSelector(selectCurrentUser);
  const isLoading = useSelector(selectAuthLoading);
  const isError = useSelector(selectAuthError);
  const isSuccess = useSelector(selectAuthSuccess);
  const message = useSelector(selectAuthMessage);

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isError && message) {
      toast.showError(message);
    }

    if (isSuccess && user) {
      toast.showSuccess('Login successful');
      navigate(from, { replace: true });
    }

    dispatch(reset());
  }, [user, isError, isSuccess, message, navigate, dispatch, toast, from]);

  const handleChange = (name, e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.username?.trim()) {
      toast.showError('Username is required');
      return;
    }
    
    if (!formData.password?.trim()) {
      toast.showError('Password is required');
      return;
    }
    
    if (formData.username.trim().length < 1) {
      toast.showError('Username must be at least 1 character');
      return;
    }
    
    if (formData.password.length < 1) {
      toast.showError('Password must be at least 1 character');
      return;
    }
    
    // Prepare clean data for submission
    const cleanFormData = {
      username: formData.username.trim(),
      password: formData.password
    };
    
    dispatch(login(cleanFormData));
  };

  return (
    <div className="min-h-screen bg-silver-mist flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo and Header Section */}
        <div className="flex flex-col items-center space-y-6">
          <div className="flex-shrink-0">
            <img 
              src={OMLogo} 
              alt="Old Mutual Logo" 
              className="h-16 w-auto"
            />
          </div>
          <div className="text-center space-y-2">
            <h1 className="text-2xl md:text-3xl font-display font-bold text-deep-jungle tracking-tight">
              Corporate Claims Portal
            </h1>
            <p className="text-steel text-sm font-medium">
              Sign in to access your account
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div 
          className="bg-crystal-white shadow-lg border border-silver-mist mx-4 sm:mx-0 overflow-hidden"
          style={{ borderRadius: '24px' }}
        >
          <div className="px-6 py-8 space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <OmTextField
                  label="Username"
                  type="text"
                  placeholder="Enter your username"
                  value={formData.username}
                  onChange={(e) => handleChange('username', e)}
                  required
                  leadingIcon="person"
                />

                <OmTextField
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e)}
                  required
                  leadingIcon="lock"
                />
              </div>

              <div className="pt-2">
                <OmButton
                  type="submit"
                  disabled={isLoading}
                  className="w-full justify-center text-base font-medium py-3 rounded-2xl"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </OmButton>
              </div>
            </form>

            {/* Test Credentials Section */}
            <div className="pt-6 border-t border-woodland-moss/20">
              <div 
                className="bg-silver-mist p-4"
                style={{ borderRadius: '24px' }}
              >
                <h4 className="text-sm font-medium text-graphite mb-3 text-center">
                  Test Credentials
                </h4>
                <div className="space-y-2 text-xs text-steel">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Admin:</span>
                    <span className="font-mono">admin / adminpassword</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">HR:</span>
                    <span className="font-mono">hr_user / hrpassword</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
